import axios from 'axios';
import { QueryClient, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AdminProductDTO,
  AdminProductsResponse,
  CreateProductDTO,
  ImageUploadFileRequest,
  ImageUploadUrl,
  ImageUploadUrlsResponse,
  UpdateProductDTO,
} from '@src/common-utils/DataModels';
import { Nullable } from '@src/common-utils/Models';
import axiosInstance, { adminProductsEndpoint } from '@src/libs/axios-util';
import { NewImageItem } from '@src/components/product-management/productForm';
import { productsInfiniteQueryKey, productsQueryKey } from '@src/queries/ProductQuery';

export const adminProductsQueryKey = ['admin-products'];

export const adminProductsPageSize = 48;

const productKeyRoots = [productsQueryKey[0], productsInfiniteQueryKey[0], adminProductsQueryKey[0]];

/**
 * True for every cached query that shows products: the admin lists, the storefront's `products` / `products-infinite`
 * queries, and the "all products" query whose key is the EMPTY array (a prefix match would miss it).
 */
export const isProductQueryKey = (queryKey: readonly unknown[]): boolean =>
  queryKey.length === 0 || productKeyRoots.includes(queryKey[0] as string);

export const invalidateProductQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ predicate: (query) => isProductQueryKey(query.queryKey) });

const productUrl = (code: string) => `${adminProductsEndpoint}/${encodeURIComponent(code)}`;

// ---------------------------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------------------------

export const useAdminProductsInfiniteQuery = ({
  searchName,
  pageSize = adminProductsPageSize,
  onError,
}: {
  searchName?: Nullable<string>;
  pageSize?: number;
  onError?: (error: Error) => void;
}) => {
  const query = useInfiniteQuery<AdminProductsResponse, Error>(
    [...adminProductsQueryKey, { searchName: searchName || '', pageSize }],
    async ({ pageParam }) => {
      const params = new URLSearchParams([['pageSize', String(pageSize)]]);
      if (searchName) {
        params.set('searchName', searchName);
      }
      if (pageParam) {
        params.set('exclusiveStartKey', pageParam);
      }

      const res = await axiosInstance.get<AdminProductsResponse>(adminProductsEndpoint, { params });
      return res.data;
    },
    {
      getNextPageParam: (lastPage) => lastPage.lastEvaluatedKey || undefined,
      retry: 1,
      onError,
    },
  );

  return {
    products: query.data?.pages.flatMap((page) => page.products) ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
    error: query.error,
  };
};

// ---------------------------------------------------------------------------------------------
// Mutations (promise based so the dialog can sequence upload -> save)
// ---------------------------------------------------------------------------------------------

const doCreateProduct = async (dto: CreateProductDTO): Promise<AdminProductDTO> =>
  (await axiosInstance.post<AdminProductDTO>(adminProductsEndpoint, dto)).data;

const doUpdateProduct = async ({ code, dto }: { code: string; dto: UpdateProductDTO }): Promise<AdminProductDTO> =>
  (await axiosInstance.patch<AdminProductDTO>(productUrl(code), dto)).data;

const doDeleteProduct = async (code: string): Promise<void> => {
  await axiosInstance.delete(productUrl(code));
};

export const useCreateProduct = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading } = useMutation(doCreateProduct, {
    onSuccess: () => invalidateProductQueries(queryClient),
  });
  return { createProduct: mutateAsync, isLoading };
};

export const useUpdateProduct = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading } = useMutation(doUpdateProduct, {
    onSuccess: () => invalidateProductQueries(queryClient),
  });
  return { updateProduct: mutateAsync, isLoading };
};

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading } = useMutation(doDeleteProduct, {
    onSuccess: () => invalidateProductQueries(queryClient),
  });
  return { deleteProduct: mutateAsync, isLoading };
};

// ---------------------------------------------------------------------------------------------
// Image upload (browser -> S3 directly with presigned PUT URLs)
// ---------------------------------------------------------------------------------------------

export const requestImageUploadUrls = async (
  code: string,
  files: ImageUploadFileRequest[],
): Promise<ImageUploadUrl[]> => {
  const res = await axiosInstance.post<ImageUploadUrlsResponse>(`${productUrl(code)}/image-upload-urls`, { files });
  return res.data.uploads;
};

/**
 * PUT one file to its presigned URL. Deliberately uses the bare `axios` (NOT `axiosInstance`): S3 must not receive
 * our Authorization header or cookies, and the shared instance's refresh/retry interceptors do not apply. Only the
 * headers returned by the API are sent (Content-Type; the browser adds Content-Length, which is part of the signature).
 */
export const uploadFileToS3 = async (
  upload: ImageUploadUrl,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> => {
  try {
    await axios.put(upload.uploadUrl, file, {
      headers: upload.headers,
      timeout: 120000,
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded * 100) / event.total));
        }
      },
    });
  } catch {
    throw new Error(`Failed to upload ${file.name}. Please try again.`);
  }
};

/** Uploads all new images and returns their storage keys by item id. Throws (nothing saved) if any upload fails. */
export const uploadNewProductImages = async (
  code: string,
  items: NewImageItem[],
  onProgress?: (itemId: string, percent: number) => void,
): Promise<Record<string, string>> => {
  if (items.length === 0) {
    return {};
  }

  const uploads = await requestImageUploadUrls(
    code,
    items.map((item) => ({ fileName: item.file.name, contentType: item.file.type, size: item.file.size })),
  );
  if (uploads.length !== items.length) {
    throw new Error('Unexpected response while preparing the image upload');
  }

  await Promise.all(
    items.map((item, index) => uploadFileToS3(uploads[index], item.file, (percent) => onProgress?.(item.id, percent))),
  );

  return Object.fromEntries(items.map((item, index) => [item.id, uploads[index].key]));
};
